// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/AgentSteps.test.tsx
import { createContext, useContext, type ReactNode } from "react";

/**
 * Cross-message artifact navigation state for the AskPuntt chat surface.
 *
 * Lets leaf rows inside `AgentSteps` reach the chat-level "which artifact is
 * currently open" answer without prop-drilling 5 layers. Consumers that
 * render outside a provider receive `null` and degrade to the pre-feature
 * non-clickable behavior — keeps legacy AgentSteps tests free of provider
 * wrapping.
 *
 * The "another artifact is being produced this turn" flag is what gates
 * D8 streaming-suppression: an in-flight tool_call whose tool is in
 * `ARTIFACT_PRODUCING_TOOLS` flips this true, and older clickable rows
 * grey out so a stale click can't yank the right pane mid-stream.
 */
export interface AskPunttArtifactContextValue {
  /** Currently-shown ticket id (drives D2 "currently viewing" highlight). */
  activeTicketId: string | null;
  /** Currently-shown canvas message id (drives D2 for canvas rows). */
  activeCanvasMessageId: string | null;
  /** Click handler for a previous `create_ticket` row. */
  onActivateTicket: (ticketId: string) => void;
  /** Click handler for a previous canvas-producing row. */
  onActivateCanvas: (messageId: string) => void;
  /**
   * True iff the in-flight turn's tool_call is producing a new artifact
   * (ticket or canvas). Drives D8 suppression on older rows.
   */
  isAnotherArtifactStreaming: boolean;
  /** Total ticket count for the "N of M" prefix in aria-labels (D5). */
  allReviewTicketIds: readonly string[];
  /** Total canvas count for the "N of M" prefix in aria-labels (D5). */
  canvasMessageIds: readonly string[];
}

const AskPunttArtifactContext =
  createContext<AskPunttArtifactContextValue | null>(null);

export function AskPunttArtifactProvider({
  value,
  children,
}: {
  value: AskPunttArtifactContextValue;
  children: ReactNode;
}) {
  return (
    <AskPunttArtifactContext.Provider value={value}>
      {children}
    </AskPunttArtifactContext.Provider>
  );
}

/**
 * Read artifact state from context. Returns `null` outside a provider so
 * AgentSteps (and its tests) can render without any wrapper and degrade to
 * the legacy non-clickable behavior.
 */
export function useAskPunttArtifactContext(): AskPunttArtifactContextValue | null {
  return useContext(AskPunttArtifactContext);
}
